import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, User, Users, Dumbbell, Award } from "lucide-react";
import { SectionLabel } from "./SectionLabel";
import { ELITE_PRICING, WORKOUT_ADDON, formatPrice, fadeUp, stagger } from "./constants";

export default function ElitePricing({
  planType, setPlanType, duration, setDuration,
  hasWorkout, setHasWorkout, basePrice, addonPrice,
  subtotal, gst, total, isProcessing, onEnroll
}) {
  return (
    <>
      <section id="pricing" className="py-24 md:py-32 bg-[#FEFDFB] text-[#0D1F17]" data-testid="elite-pricing">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-14">
            <SectionLabel>Investment in Your Health</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#0D1F17] mt-4">Choose Your Plan</h2>
          </motion.div>

          {/* Plan Toggle */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex bg-[#0D1F17] border border-[#D4AF37]/20 rounded-full p-1" data-testid="plan-toggle">
              {["individual", "couple"].map((type) => (
                <button key={type} onClick={() => setPlanType(type)} data-testid={`toggle-${type}`}
                  className={`flex items-center gap-2 px-7 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                    planType === type ? "bg-[#D4AF37] text-[#0D1F17]" : "text-[#F7F5F0]/60 hover:text-[#F7F5F0]"
                  }`}>
                  {type === "individual" ? <User size={14} /> : <Users size={14} />}
                  <span className="capitalize">{type}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Duration Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {Object.entries(ELITE_PRICING).map(([dur, prices], i) => {
              const price = prices[planType];
              const isSelected = duration === dur;
              const perMonth = dur === "3 Months" ? Math.round(price / 3) : dur === "6 Months" ? Math.round(price / 6) : null;
              return (
                <motion.button key={dur} {...stagger(i)} onClick={() => setDuration(dur)}
                  data-testid={`duration-${dur.replace(/\s/g, "-").toLowerCase()}`}
                  className={`relative text-left p-8 border rounded-2xl transition-all duration-300 shadow-sm ${
                    isSelected ? "bg-[#0D1F17] text-[#F7F5F0] border-[#D4AF37]/40 shadow-xl shadow-[#0D1F17]/20" : "bg-white border-[#0D1F17]/8 hover:border-[#D4AF37]/30"
                  }`}>
                  {dur === "3 Months" && (
                    <span className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold tracking-widest uppercase px-4 py-1 rounded-full ${
                      isSelected ? "bg-[#D4AF37] text-[#0D1F17]" : "bg-[#0D1F17] text-[#F7F5F0]"
                    }`}>Popular</span>
                  )}
                  <p className={`text-xs tracking-wider uppercase mb-2 ${isSelected ? "text-[#D4AF37]" : "text-[#0D1F17]/40"}`}>{dur}</p>
                  <p className="font-serif text-3xl">{"\u20B9"}{formatPrice(price)}</p>
                  {perMonth && (
                    <p className={`text-xs mt-1 ${isSelected ? "text-[#F7F5F0]/50" : "text-[#0D1F17]/40"}`}>
                      {"\u2248"} {"\u20B9"}{formatPrice(perMonth)}/month
                    </p>
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* Workout Add-on */}
          <motion.div {...fadeUp} data-testid="workout-addon"
            className={`mt-8 max-w-4xl mx-auto p-6 rounded-2xl border cursor-pointer transition-all duration-300 shadow-sm ${
              hasWorkout ? "bg-[#0D1F17]/5 border-[#D4AF37]/30" : "bg-white border-[#0D1F17]/8 hover:border-[#D4AF37]/20"
            }`} onClick={() => setHasWorkout(!hasWorkout)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                  hasWorkout ? "bg-[#D4AF37]/15 border border-[#D4AF37]/30" : "border border-[#0D1F17]/10"
                }`}>
                  <Dumbbell size={18} className={hasWorkout ? "text-[#D4AF37]" : "text-[#0D1F17]/40"} />
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h4 className="font-serif text-base text-[#0D1F17]">One-to-One Workout Sessions</h4>
                    <span className="text-[10px] tracking-widest uppercase bg-[#D4AF37]/10 text-[#D4AF37] px-3 py-0.5 rounded-full border border-[#D4AF37]/20 font-semibold">Add-on</span>
                  </div>
                  <p className="text-xs text-[#0D1F17]/40 mt-0.5">Personalized workout sessions — 4 days/week</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-serif text-xl text-[#0D1F17]">{"\u20B9"}{formatPrice(WORKOUT_ADDON[planType])}</p>
                <button onClick={(e) => { e.stopPropagation(); setHasWorkout(!hasWorkout); }} data-testid="btn-toggle-workout"
                  className={`mt-1 text-xs font-semibold px-5 py-1.5 rounded-full transition-all ${
                    hasWorkout ? "bg-[#D4AF37] text-[#0D1F17]" : "border border-[#0D1F17]/15 text-[#0D1F17] hover:border-[#D4AF37]/30"
                  }`}>{hasWorkout ? "Added" : "Add"}</button>
              </div>
            </div>
          </motion.div>

          {/* Order Summary */}
          <motion.div {...fadeUp} data-testid="order-summary"
            className="mt-10 max-w-4xl mx-auto p-8 rounded-2xl bg-[#0D1F17] text-[#F7F5F0] shadow-xl">
            <h3 className="font-serif text-lg text-[#F7F5F0] mb-5">Order Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <div>
                  <span className="text-[#F7F5F0]">Fitly Elite — {duration}</span>
                  <span className="text-[#A3B8AD] ml-2 text-xs">({planType === "couple" ? "Couple" : "Individual"})</span>
                </div>
                <span className="text-[#F7F5F0] font-medium">{"\u20B9"}{formatPrice(basePrice)}</span>
              </div>
              {hasWorkout && (
                <div className="flex justify-between">
                  <span className="text-[#F7F5F0]">Workout Sessions <span className="text-[#A3B8AD] text-xs">({planType === "couple" ? "Couple" : "Individual"})</span></span>
                  <span className="text-[#F7F5F0] font-medium">{"\u20B9"}{formatPrice(addonPrice)}</span>
                </div>
              )}
              <div className="border-t border-[#F7F5F0]/10 pt-3 space-y-2">
                <div className="flex justify-between" data-testid="summary-subtotal">
                  <span className="text-[#A3B8AD]">Subtotal</span><span className="text-[#F7F5F0]">{"\u20B9"}{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between" data-testid="summary-gst">
                  <span className="text-[#A3B8AD]">GST (18%)</span><span className="text-[#F7F5F0]">{"\u20B9"}{formatPrice(gst)}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-[#F7F5F0]/10">
                  <span className="font-serif text-lg text-[#D4AF37]">Total</span>
                  <span className="font-serif text-2xl text-[#F7F5F0]" data-testid="summary-total">{"\u20B9"}{formatPrice(total)}</span>
                </div>
              </div>
            </div>
            <button onClick={onEnroll}
              data-testid="btn-enroll" disabled={isProcessing}
              className="w-full mt-6 bg-[#D4AF37] text-[#0D1F17] py-4 text-sm font-bold tracking-wide rounded-full hover:bg-[#B5952F] transition-all disabled:opacity-50 shadow-lg shadow-[#D4AF37]/20">
              {isProcessing ? "Processing..." : `Enroll Now — \u20B9${formatPrice(total)}`}
            </button>
            <p className="text-center text-[#A3B8AD]/40 text-xs mt-3">Secure payments powered by Razorpay</p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-28 bg-[#0D1F17] text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-[#132A20]/50 to-transparent" />
        <div className="relative z-10">
          <motion.div {...fadeUp}>
            <Award size={36} className="text-[#D4AF37] mx-auto mb-5" />
            <h2 className="font-serif text-3xl sm:text-4xl text-[#F7F5F0] mb-3">Ready to start your journey?</h2>
            <p className="text-[#A3B8AD] mb-8 max-w-md mx-auto text-sm">
              Have questions before enrolling? Book a consultation call with our team.
            </p>
            <a href="/consultation" data-testid="cta-book-consultation"
              className="inline-flex items-center gap-2 border border-[#D4AF37]/30 text-[#D4AF37] px-10 py-4 text-sm font-semibold tracking-wide rounded-full hover:bg-[#D4AF37] hover:text-[#0D1F17] transition-all">
              Book Consultation <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>
    </>
  );
}
